import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Repeat2, Sparkles, Swords } from 'lucide-react';
import { playPracticeSound, setPracticeSoundEnabled, unlockPracticeAudio } from '../services/soundEffects';
import { getSceneImage } from '../services/SceneAssetRegistry';
import { normalizeCharacterId } from '../services/TournamentAssets';
import MarkdownText from './MarkdownText';
import PointBonusTip, { POINT_BONUS_FEEDBACK_MS } from './PointBonusTip';
import CardListeningControl from './CardListeningControl';
import LearningSessionHeader from './LearningSessionHeader';
import LearningCoach from './LearningCoach';
import RepeatPracticeCard from './RepeatPracticeCard';
import FlashCard from './FlashCard';
import CardJudgmentFeedback, { COMPACT_JUDGMENT_FEEDBACK_MS } from './CardJudgmentFeedback';
import LearningCardProgress, { RepeatActionButton } from './LearningCardProgress';
import PracticeSettingsService from '../services/PracticeSettingsService';
import useRepeatInputLock from '../hooks/useRepeatInputLock';
import { lockBodyScroll } from '../utils/bodyScrollLock';
import { t } from '../i18n';
import './NpcEncounterOverlay.css';

const CARD_TIMER_SECONDS = 15;
export const ENCOUNTER_PRACTICE_REPEAT_TARGET = 8;

export function calculateNpcEncounterResult({ opponentHp = 100, userHp = 100 } = {}) {
  if (opponentHp <= 0) {
    return {
      outcome: 'win',
      label: 'Perfect Win',
      scoreDelta: 100,
      sceneOutcome: 'win',
    };
  }

  if (opponentHp <= 20) {
    return {
      outcome: 'win',
      label: 'Win',
      scoreDelta: 50,
      sceneOutcome: 'win',
    };
  }

  if (userHp <= 0) {
    return {
      outcome: 'lose',
      label: 'Perfect Lose',
      scoreDelta: -100,
      sceneOutcome: 'lose',
    };
  }

  if (userHp <= 20) {
    return {
      outcome: 'lose',
      label: 'Lose',
      scoreDelta: -50,
      sceneOutcome: 'lose',
    };
  }

  return {
    outcome: 'tie',
    label: 'Tie',
    scoreDelta: 0,
    sceneOutcome: 'tie',
  };
}

function hpPercent(value) {
  return `${Math.max(0, Math.min(100, Number(value) || 0))}%`;
}

function previousPracticeDays(card) {
  const timestamp = PracticeSettingsService.getLastPracticeTimestamp(card);
  if (!timestamp) return null;
  const previous = new Date(timestamp);
  if (Number.isNaN(previous.getTime())) return null;
  const today = new Date();
  previous.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - previous) / 86400000));
}

function NpcEncounterOverlay({
  cards = [],
  scene = null,
  onComplete,
  onPracticeRepeat,
  onAnswerViewed,
  onLevelUp,
  onSkip,
  isPaused = false,
  canUseTts = true,
  onMembershipRequired,
  reviewMode = false,
  elapsedSeconds = 0,
  sessionScoreGained = 0,
  onCardResult,
  reviewNotice = null,
  onRepeatLockChange,
  coachingScenes = [],
}) {
  const [cardIndex, setCardIndex] = useState(0);
  const [transition, setTransition] = useState('enter');
  const transitionTimerRef = useRef(null);
  const transitioningRef = useRef(true);
  const reducedMotion = useRef(Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  const animateCard = useCallback((kind, commit) => {
    clearTimeout(transitionTimerRef.current);
    if (reducedMotion.current) {
      commit?.();
      transitioningRef.current = false;
      setTransition(null);
      return;
    }
    transitioningRef.current = true;
    setTransition(kind);
    transitionTimerRef.current = setTimeout(() => {
      commit?.();
      if (kind === 'flip-out') {
        setTransition('flip-in');
        transitionTimerRef.current = setTimeout(() => {
          transitioningRef.current = false;
          setTransition(null);
        }, 180);
      } else {
        transitioningRef.current = false;
        setTransition(null);
      }
    }, kind === 'flip-out' ? 180 : 320);
  }, []);
  const [opponentHp, setOpponentHp] = useState(100);
  const [userHp, setUserHp] = useState(100);
  const [revealedCardKey, setRevealedCardKey] = useState(null);
  const [revealRequest, setRevealRequest] = useState(null);
  const [timeLeft, setTimeLeft] = useState(CARD_TIMER_SECONDS);
  const [pendingOutcome, setPendingOutcome] = useState(null);
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [practiceRepeatCount, setPracticeRepeatCount] = useState(0);
  const [repeatFeedback, setRepeatFeedback] = useState(null);
  const [pointFeedback, setPointFeedback] = useState(null);
  const { locked: repeatLocked, begin: beginRepeatLock, finish: finishRepeatLock } = useRepeatInputLock();
  const viewedCardKeysRef = useRef(new Set());
  const [judgment, setJudgment] = useState(null);
  const passedCardIdsRef = useRef([]);
  const resolvedRef = useRef(false);
  const countdownTimerRef = useRef(null);
  const judgmentTimerRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const pendingAdvanceRef = useRef(null);
  const pausedRef = useRef(isPaused);
  pausedRef.current = isPaused;
  const cardWrapRef = useRef(null);
  const practiceRepeatCountRef = useRef(0);
  const currentCard = cards[cardIndex] || cards[0] || null;
  const currentCardKey = `${cardIndex}:${currentCard?.id || currentCard?._id || ''}`;
  const answerViewedRef = useRef(null);
  const pendingAnswerViewedRef = useRef(null);
  answerViewedRef.current = { card: currentCard, handler: onAnswerViewed };
  const handleAnswerViewed = useCallback(() => {
    if (pausedRef.current) {
      pendingAnswerViewedRef.current = currentCardKey;
      return;
    }
    pendingAnswerViewedRef.current = null;
    transitioningRef.current = false;
    setTransition(null);
    setRevealedCardKey(currentCardKey);
    if (viewedCardKeysRef.current.has(currentCardKey)) return;
    viewedCardKeysRef.current.add(currentCardKey);
    const reward = answerViewedRef.current.handler?.({ card: answerViewedRef.current.card });
    setPointFeedback({ id: `${currentCardKey}:view`, label: t('practiceUi.encounter.answerViewedBonus'),
      points: reward?.sessionScoreDelta ?? 2 });
  }, [currentCardKey]);

  useEffect(() => {
    if (!isPaused && pendingAnswerViewedRef.current === currentCardKey) handleAnswerViewed();
  }, [isPaused, currentCardKey, handleAnswerViewed]);

  useEffect(() => {
    if (!pointFeedback) return undefined;
    const timer = setTimeout(() => setPointFeedback(null), POINT_BONUS_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [pointFeedback]);
  useEffect(() => { onRepeatLockChange?.(repeatLocked); }, [onRepeatLockChange, repeatLocked]);
  useEffect(() => () => { onRepeatLockChange?.(false); }, [onRepeatLockChange]);
  // A new card must never inherit the previous card's answer visibility,
  // even during the render before the reset effect runs.
  const isRevealed = revealedCardKey === currentCardKey;
  const hasCurrentCard = Boolean(currentCard);
  const opponentFrame = scene?.frames?.find((frame) => frame?.image) || scene?.frames?.[0] || null;
  const opponentName = opponentFrame?.name || 'NPC Challenger';
  const opponentCharacter = [opponentFrame?.actor, opponentFrame?.image, opponentFrame?.name]
    .map(normalizeCharacterId).find(id => ['Don', 'Twig', 'Pearl'].includes(id)) || 'default';
  const coachingTrigger = `${reviewMode ? 'review' : 'encounter'}.${isPracticeMode ? 'repeat' : isRevealed ? 'answer' : 'question'}`;
  const coachingActor = isPracticeMode ? 'trainer.min' : opponentCharacter === 'default' ? (opponentFrame?.actor || 'default') : `player.${opponentCharacter}`;
  const coachingSpeaker = { name: opponentName, image: opponentFrame?.imageUrl || getSceneImage(opponentFrame?.image) };
  const hasTimedOut = pendingOutcome === 'timeout';
  const isOpponentCritical = !reviewMode && opponentHp <= 30;
  const isUserCritical = !reviewMode && userHp <= 30;
  const canLevelUpCurrentCard = Boolean(onLevelUp) &&
    PracticeSettingsService.isCardUpgradeable(currentCard, { allowGraduated: true });
  const isCardAdvancing = isPracticeMode
    ? practiceRepeatCount >= ENCOUNTER_PRACTICE_REPEAT_TARGET
    : resolvedRef.current;

  useEffect(() => {
    if (!hasCurrentCard) return undefined;
    return lockBodyScroll();
  }, [hasCurrentCard]);

  useEffect(() => {
    const cardWrap = cardWrapRef.current;
    if (!cardWrap) return undefined;
    // Fit the text only when content or available space changes, never on timer ticks.
    const fitText = () => {
      cardWrap.querySelectorAll('.npc-card-text, .flashcard-encounter .flashcard-content').forEach((text) => {
        let size = 24;
        text.style.fontSize = `${size}px`;
        while (size > 12 && (text.scrollHeight > text.clientHeight || text.scrollWidth > text.clientWidth)) {
          size -= 1;
          text.style.fontSize = `${size}px`;
        }
      });
    };
    fitText();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(fitText) : null;
    observer?.observe(cardWrap);
    window.addEventListener('resize', fitText);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', fitText);
    };
  }, [currentCard, isRevealed, isPracticeMode, repeatFeedback?.id]);

  const clearCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const handleRevealStart = useCallback(() => {
    clearCountdownTimer();
    transitioningRef.current = true;
    setTransition('card-flip');
    void unlockPracticeAudio();
    void playPracticeSound('repeat');
  }, [clearCountdownTimer]);

  const clearJudgmentTimer = useCallback(() => {
    if (judgmentTimerRef.current) {
      clearTimeout(judgmentTimerRef.current);
      judgmentTimerRef.current = null;
    }
  }, []);

  const queueAdvance = useCallback((callback) => {
    clearTimeout(advanceTimerRef.current);
    pendingAdvanceRef.current = callback;
    advanceTimerRef.current = setTimeout(() => {
      if (pausedRef.current) return;
      pendingAdvanceRef.current = null;
      callback();
    }, 520);
  }, []);

  useEffect(() => {
    if (isPaused) clearTimeout(advanceTimerRef.current);
    else if (pendingAdvanceRef.current) queueAdvance(pendingAdvanceRef.current);
  }, [isPaused, queueAdvance]);

  const showJudgment = useCallback((result, reward) => {
    clearJudgmentTimer();
    setPointFeedback(null);
    setJudgment({ result, points: Math.max(0, Number(reward?.sessionScoreDelta) || 0) });
    judgmentTimerRef.current = setTimeout(() => {
      judgmentTimerRef.current = null;
      setJudgment(null);
    }, COMPACT_JUDGMENT_FEEDBACK_MS);
  }, [clearJudgmentTimer]);

  const finishEncounter = useCallback((nextOpponentHp, nextUserHp, nextPassedCardIds) => {
    const result = reviewMode ? { outcome: 'review', scoreDelta: 0 } : calculateNpcEncounterResult({
      opponentHp: nextOpponentHp,
      userHp: nextUserHp,
    });
    onComplete?.({
      ...result,
      opponentHp: nextOpponentHp,
      userHp: nextUserHp,
      passedCardIds: nextPassedCardIds,
      cardsSeen: cardIndex + 1,
    });
  }, [cardIndex, onComplete, reviewMode]);

  const applyAnswer = useCallback((isOk, { timedOut = false } = {}) => {
    if (isPaused || transitioningRef.current || resolvedRef.current || !isRevealed || (isOk && hasTimedOut)) return;
    resolvedRef.current = true;
    clearJudgmentTimer();
    clearCountdownTimer();
    const reward = onCardResult?.({ card: currentCard, passed: isOk });

    const cardId = currentCard?.id || currentCard?._id;
    const nextPassedCardIds = isOk && cardId
      ? [...passedCardIdsRef.current, cardId]
      : passedCardIdsRef.current;
    passedCardIdsRef.current = nextPassedCardIds;

    const nextOpponentHp = isOk && !reviewMode ? Math.max(0, opponentHp - 10) : opponentHp;
    const nextUserHp = isOk || reviewMode ? userHp : Math.max(0, userHp - 20);

    setOpponentHp(nextOpponentHp);
    setUserHp(nextUserHp);
    setPendingOutcome(timedOut ? 'timeout' : isOk ? 'ok' : 'no');
    void playPracticeSound(isOk ? 'attackSuccess' : 'attackDamage');
    showJudgment(isOk ? 'correct' : 'retry', reward);

    const shouldEnd = (!reviewMode && (nextOpponentHp <= 0 || nextUserHp <= 0)) ||
      cardIndex >= Math.min(cards.length, reviewMode ? 100 : 10) - 1;
    queueAdvance(() => {
      if (shouldEnd) {
        finishEncounter(nextOpponentHp, nextUserHp, nextPassedCardIds);
        return;
      }
      setCardIndex((current) => current + 1);
    });
  }, [cardIndex, cards.length, clearCountdownTimer, clearJudgmentTimer, currentCard, finishEncounter, hasTimedOut, isPaused, isRevealed, onCardResult, opponentHp, queueAdvance, reviewMode, showJudgment, userHp]);

  const beginPracticeMode = useCallback(({ timedOut = false } = {}) => {
    if (isPaused || transitioningRef.current || resolvedRef.current || isPracticeMode || !isRevealed) return;
    resolvedRef.current = true;
    clearCountdownTimer();
    clearJudgmentTimer();

    const reward = onCardResult?.({ card: currentCard, passed: false });
    const nextUserHp = reviewMode ? userHp : Math.max(0, userHp - 20);
    setUserHp(nextUserHp);
    setPendingOutcome(timedOut ? 'timeout' : 'no');
    setIsPracticeMode(true);
    animateCard('repeat');
    practiceRepeatCountRef.current = 0;
    setPracticeRepeatCount(0);
    void playPracticeSound('attackDamage');
    showJudgment('retry', reward);
  }, [animateCard, clearCountdownTimer, clearJudgmentTimer, currentCard, isPaused, isPracticeMode, isRevealed, onCardResult, reviewMode, showJudgment, userHp]);

  const handlePracticeRepeat = useCallback(() => {
    if (isPaused || transitioningRef.current || !isPracticeMode || practiceRepeatCountRef.current >= ENCOUNTER_PRACTICE_REPEAT_TARGET) return;
    if (reviewMode && !beginRepeatLock()) return;

    try {
      const nextRepeatCount = practiceRepeatCountRef.current + 1;
      practiceRepeatCountRef.current = nextRepeatCount;
      setPracticeRepeatCount(nextRepeatCount);
      void playPracticeSound('repeat', {
        repeatIndex: nextRepeatCount,
        targetRepeats: ENCOUNTER_PRACTICE_REPEAT_TARGET,
        recoverOnGesture: true,
      });

      const repeatResult = onPracticeRepeat?.({
        card: currentCard,
        repeatCount: nextRepeatCount,
        targetRepeats: ENCOUNTER_PRACTICE_REPEAT_TARGET,
      });
      const returnedPointDelta = Number(repeatResult?.sessionScoreDelta);
      setRepeatFeedback({
        id: `${cardIndex}-${nextRepeatCount}`,
        points: Number.isFinite(returnedPointDelta) ? returnedPointDelta : 1,
      });
      setPointFeedback({ id: `${currentCardKey}:repeat:${nextRepeatCount}`,
        label: t('practiceUi.encounter.repeatBonus'), points: Number.isFinite(returnedPointDelta) ? returnedPointDelta : 1 });

      if (nextRepeatCount < ENCOUNTER_PRACTICE_REPEAT_TARGET) return;

      const shouldEnd = (!reviewMode && userHp <= 0) || cardIndex >= Math.min(cards.length, reviewMode ? 100 : 10) - 1;
      queueAdvance(() => {
        if (shouldEnd) {
          finishEncounter(opponentHp, userHp, passedCardIdsRef.current);
          return;
        }
        setCardIndex((current) => current + 1);
      });
    } finally {
      if (reviewMode) finishRepeatLock();
    }
  }, [beginRepeatLock, cardIndex, cards.length, currentCard, currentCardKey, finishEncounter, finishRepeatLock, isPaused, isPracticeMode, onPracticeRepeat, opponentHp, queueAdvance, reviewMode, userHp]);

  useEffect(() => () => {
    clearTimeout(advanceTimerRef.current);
    clearCountdownTimer();
    clearJudgmentTimer();
  }, [clearCountdownTimer, clearJudgmentTimer]);

  useEffect(() => {
    resolvedRef.current = false;
    animateCard('enter');
    setRevealedCardKey(null);
    setRevealRequest(null);
    pendingAnswerViewedRef.current = null;
    setTimeLeft(CARD_TIMER_SECONDS);
    setPendingOutcome(null);
    setIsPracticeMode(false);
    practiceRepeatCountRef.current = 0;
    setPracticeRepeatCount(0);
    setRepeatFeedback(null);
    setPointFeedback(null);
    setJudgment(null);
    clearCountdownTimer();
    clearJudgmentTimer();

    let remainingMs = CARD_TIMER_SECONDS * 1000 + (reducedMotion.current ? 0 : 320);
    let lastTickAt = Date.now();
    countdownTimerRef.current = setInterval(() => {
      const now = Date.now();
      const tickDuration = now - lastTickAt;
      lastTickAt = now;
      if (pausedRef.current) return;
      remainingMs = Math.max(0, remainingMs - tickDuration);
      const nextSeconds = Math.min(CARD_TIMER_SECONDS, Math.ceil(remainingMs / 1000));
      setTimeLeft(nextSeconds);

      if (remainingMs <= 0) {
        clearCountdownTimer();
        setPendingOutcome('timeout');
        setRevealRequest({ id: currentCardKey, direction: 'right' });
      }
    }, 120);

    return () => {
      clearTimeout(transitionTimerRef.current);
      clearCountdownTimer();
      clearJudgmentTimer();
    };
  }, [animateCard, currentCardKey, clearCountdownTimer, clearJudgmentTimer]);

  if (!currentCard) {
    return null;
  }

  const listeningControl = <CardListeningControl
    key={currentCardKey}
    cardId={currentCard?.id || currentCard?._id}
    ttsText={currentCard?.ttsText}
    isAnswerVisible={isRevealed}
    controlMode={reviewMode ? (isRevealed || isPracticeMode ? 'playback' : 'toggle') : 'auto'}
    onAutoplayEnabledChange={reviewMode ? setPracticeSoundEnabled : undefined}
    visitToken={cardIndex}
    canUseTts={canUseTts}
    onMembershipRequired={onMembershipRequired}
    interactionDisabled={repeatLocked}
  />;
  const levelUpControl = canLevelUpCurrentCard && (reviewMode || isRevealed) ? <button
    type="button"
    className="npc-level-up-button"
    aria-label="Level up this card"
    title="Level up this card"
    disabled={isPaused || repeatLocked || Boolean(transition) || isCardAdvancing}
    onClick={() => {
      if (!isPaused && !repeatLocked && !isCardAdvancing) onLevelUp(currentCard);
    }}
  ><Sparkles size={20} strokeWidth={2.2} aria-hidden="true" /></button> : null;

  return (
    <div
      className={[
        'npc-encounter-overlay',
        isOpponentCritical || isUserCritical ? 'is-critical' : '',
        isOpponentCritical ? 'is-opponent-critical' : '',
        isUserCritical ? 'is-user-critical' : '',
        isPracticeMode ? 'is-practice-mode' : '',
        reviewMode ? 'is-review-mode' : '',
      ].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal="true"
      aria-label={reviewMode ? t('practiceUi.reviewTraining.title') : 'NPC encounter'}
      aria-hidden={isPaused || undefined}
      inert={isPaused ? '' : undefined}
    >
      <div className="npc-encounter-fire" aria-hidden="true" />
      <div className="npc-encounter-panel">
        {reviewMode ? (
          <LearningSessionHeader
            className="npc-review-header"
            title={<h1>{t('practiceUi.reviewTraining.title')}</h1>}
            elapsedSeconds={elapsedSeconds}
            timeLimitSeconds={30 * 60}
            onExit={onSkip}
            exitLabel={t('practiceUi.reviewTraining.end')}
            exitDisabled={isPaused || repeatLocked}
            leftTools={listeningControl}
            rightTools={levelUpControl}
          />
        ) : <div className="npc-encounter-topbar">
          <div className={`npc-fighter npc-fighter-opponent ${isOpponentCritical ? 'is-critical' : ''}`.trim()}>
            <span className="npc-fighter-name">{opponentName}</span>
            <div className="npc-hp-track" aria-label={`Opponent HP ${opponentHp}`}>
              <div className="npc-hp-fill npc-hp-fill-opponent" style={{ width: hpPercent(opponentHp) }} />
            </div>
            <span className="npc-hp-value">{opponentHp}</span>
          </div>
          <div className="npc-versus">
            <Swords size={24} strokeWidth={2.4} aria-hidden="true" />
          </div>
          <div className={`npc-fighter npc-fighter-user ${isUserCritical ? 'is-critical' : ''}`.trim()}>
            <span className="npc-fighter-name">Me</span>
            <div className="npc-hp-track" aria-label={`My HP ${userHp}`}>
              <div className="npc-hp-fill npc-hp-fill-user" style={{ width: hpPercent(userHp) }} />
            </div>
            <span className="npc-hp-value">{userHp}</span>
          </div>
        </div>}

        {reviewNotice && <p className="npc-review-notice" role="alert">{reviewNotice}</p>}
        <div className="npc-monster-stage is-coaching">
          <LearningCoach scenes={coachingScenes} trigger={coachingTrigger} visitKey={currentCardKey}
            actor={coachingActor} speaker={coachingSpeaker}
            variables={{ currentIndex: cardIndex + 1, totalCount: cards.length }} />
            {!reviewMode && <div className="npc-monster-tools" role="group" aria-label="Encounter card tools">
              {listeningControl}
              {levelUpControl}
            </div>}
        </div>

        <div className="npc-encounter-workout">
          <div className={`npc-encounter-card-wrap${transition ? ` npc-transition-${transition}` : ''}`} ref={cardWrapRef} aria-busy={Boolean(transition) || repeatLocked}>
            {!isPracticeMode && <span
              className={`npc-timer npc-recall-timer ${timeLeft <= 2 ? 'is-danger' : ''} ${hasTimedOut ? 'is-timeout' : ''}`.trim()}
              role="timer"
              aria-label={t('practiceUi.encounter.timerLabel')}
              aria-live={hasTimedOut ? 'polite' : 'off'}
            >{hasTimedOut ? t('practiceUi.encounter.timeOut') : timeLeft}</span>}
            {!isPracticeMode ? <FlashCard
              key={currentCardKey}
              card={currentCard}
              interactionMode="encounter"
              revealRequest={revealRequest}
              answerOkDisabled={hasTimedOut}
              answerChoiceAvailable={!resolvedRef.current}
              interactionDisabled={isPaused || Boolean(transition) || isCardAdvancing}
              keyboardShortcutsEnabled={!isPaused}
              onReveal={handleRevealStart}
              onAnswerViewed={handleAnswerViewed}
              onAnswerOk={() => applyAnswer(true)}
              onAnswerNo={() => beginPracticeMode({ timedOut: hasTimedOut })}
            /> : reviewMode ? <RepeatPracticeCard
              question={currentCard.front || ''}
              answer={currentCard.back || ''}
              memo={currentCard.memo}
              backMemo={currentCard.backMemo}
              onRepeat={handlePracticeRepeat}
              disabled={isPaused || repeatLocked || Boolean(transition) || isCardAdvancing}
              repeatCount={practiceRepeatCount}
              targetRepeats={ENCOUNTER_PRACTICE_REPEAT_TARGET}
              repeatLabel="Question and answer card"
            /> : <button
              className="npc-encounter-card npc-card-mode-repeat is-revealed"
              type="button"
              aria-label="Question and answer card"
              disabled={isPaused || Boolean(transition) || isCardAdvancing}
              onClick={handlePracticeRepeat}
            >
              <div
                key={`npc-repeat-motion-${repeatFeedback?.id || 'idle'}`}
                className={`npc-card-motion ${repeatFeedback ? 'is-repeat-feedback' : ''}`.trim()}
              >
                <div className="npc-card-face npc-card-front">
                  <span className="npc-card-caption" aria-hidden="true">Q</span>
                  <span className="npc-timer is-practice" role="status"
                    aria-label={t('practiceUi.encounter.repeatLabel')}>
                    {t('practiceUi.encounter.repeatProgress', undefined, { count: practiceRepeatCount, total: ENCOUNTER_PRACTICE_REPEAT_TARGET })}
                  </span>
                  <div className="npc-card-body">
                    <MarkdownText className="npc-card-text" text={currentCard.front || ''} />
                    {currentCard.memo && <MarkdownText className="npc-card-memo" text={currentCard.memo} />}
                  </div>
                </div>
                <div className="npc-card-face npc-card-back">
                  <span className="npc-card-caption" aria-hidden="true">A</span>
                  <div className="npc-card-body">
                    <MarkdownText className="npc-card-text" text={currentCard.back || ''} />
                    {currentCard.backMemo && <MarkdownText className="npc-card-memo" text={currentCard.backMemo} />}
                  </div>
                </div>
              </div>
            </button>}
            <CardJudgmentFeedback result={judgment?.result} points={judgment?.points} compact />
            {pointFeedback?.points > 0 && (
              <div className="npc-point-feedback" aria-live="polite">
                <PointBonusTip bonuses={[pointFeedback]} eventId={pointFeedback.id} />
              </div>
            )}
          </div>

          {reviewMode && isPracticeMode && <LearningCardProgress
            count={practiceRepeatCount}
            total={ENCOUNTER_PRACTICE_REPEAT_TARGET}
            points={sessionScoreGained}
            grade={PracticeSettingsService.getAssessmentGrade(currentCard)}
            level={PracticeSettingsService.getCardLevel(currentCard) > 1 ? PracticeSettingsService.getCardLevel(currentCard) : undefined}
            daysSince={previousPracticeDays(currentCard)}
            ariaLabel={t('interface.practice.cardProgress')}
          />}
          {isPracticeMode && <div className="npc-encounter-actions">
            {reviewMode ? <RepeatActionButton
              className="npc-review-repeat-action"
              onClick={handlePracticeRepeat}
              disabled={isPaused || repeatLocked || Boolean(transition) || practiceRepeatCount >= ENCOUNTER_PRACTICE_REPEAT_TARGET}
              label={t('writingExam.step3')}
              stepLabel={t('writingExam.stepPosition', undefined, { step: 3 })}
              aria-label={`Repeat answer ${practiceRepeatCount} of ${ENCOUNTER_PRACTICE_REPEAT_TARGET}`}
            /> : <button
              type="button"
              className="npc-action npc-action-practice"
              onClick={handlePracticeRepeat}
              disabled={isPaused || Boolean(transition) || practiceRepeatCount >= ENCOUNTER_PRACTICE_REPEAT_TARGET}
              aria-label={`Repeat answer ${practiceRepeatCount} of ${ENCOUNTER_PRACTICE_REPEAT_TARGET}`}
            >
              <Repeat2 size={26} strokeWidth={2.6} aria-hidden="true" />
              <span>{t('practiceUi.encounter.repeatProgress', undefined, { count: practiceRepeatCount, total: ENCOUNTER_PRACTICE_REPEAT_TARGET })}</span>
            </button>}
          </div>}

          {pendingOutcome && !isPracticeMode && (
            <div className={`npc-outcome npc-outcome-${pendingOutcome}`}>
              {t(`practiceUi.reviewTraining.${pendingOutcome === 'ok' ? 'correct' : 'needsRepeat'}`)}
            </div>
          )}

          {!reviewMode && !isPracticeMode && (
            <button type="button" className="npc-encounter-skip" onClick={onSkip} disabled={isPaused}>
              Escape
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default NpcEncounterOverlay;
